/**
 * Import Modal Component
 * 
 * Main modal component for the 3-step transaction import process
 * Step 1: File Upload
 * Step 2: Column Mapping
 * Step 3: Review and Import
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Pencil, Check } from 'lucide-react';
import { ImportStep1FileUpload } from './ImportStep1FileUpload';
import { ImportStep2ColumnMapping } from './ImportStep2ColumnMapping';
import { ImportStep3ReviewImport } from './ImportStep3ReviewImport';
import { ImportStep4Complete } from './ImportStep4Complete';
import { ManualTransactionForm } from './ManualTransactionForm';
import { transactionImportService } from '../../services/transaction-import-service';
import { hybridAccountService } from '../../services/hybrid-account-service';
import type { ColumnMapping } from '../../types/import.types';
import type { Account } from '../../types/account.types';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountId?: string; // Optional - if provided, pre-selects account
  accountDisabled?: boolean; // Optional - if true, disables account selection (default: false)
  onImportComplete?: () => void; // Callback when import completes
}

export function ImportModal({
  isOpen,
  onClose,
  accountId,
  accountDisabled = false,
  onImportComplete
}: ImportModalProps): React.ReactElement {
  const [mode, setMode] = useState<'automatic' | 'manual'>('automatic');
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvColumns, setCsvColumns] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<string[][]>([]);
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>(accountId || '');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [importResult, setImportResult] = useState<{
    imported: number;
    failed: number;
    errors: string[];
    warnings?: string[];
    importSessionId?: string;
    totalRows?: number;
    skippedDuplicates?: number;
    skippedZeroAmounts?: number;
  } | null>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);

  // Fetch accounts when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAccounts();
    }
  }, [isOpen]);

  // Sync selectedAccountId with accountId prop when it changes
  useEffect(() => {
    if (accountId) {
      setSelectedAccountId(accountId);
    }
  }, [accountId]);

  // Scroll to top when step changes
  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const result = await hybridAccountService.getAccounts({
        status: 'active'
      });
      if (result.error) {
        console.error('Failed to load accounts:', result.error.message);
        setAccounts([]);
      } else if (result.data) {
        setAccounts(result.data.accounts || []);
      } else {
        setAccounts([]);
      }
    } catch (error) {
      console.error('Failed to load accounts:', error);
      setAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  if (!isOpen) return <></>;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleFileSelected = async (file: File | null) => {
    setSelectedFile(file);
    if (file) {
      // Parse CSV file
      const result = await transactionImportService.parseCSVFile(file);
      if (result.data) {
        setCsvColumns(result.data.columns);
        setCsvRows(result.data.rows);
      }
    } else {
      setCsvColumns([]);
      setCsvRows([]);
    }
  };

  const handleStep1Next = () => {
    if (selectedFile && csvColumns.length > 0) {
      setCurrentStep(2);
    }
  };

  const handleStep2Next = () => {
    if (mappings.length > 0) {
      setCurrentStep(3);
      // Scroll to top when moving to next step
      setTimeout(() => {
        if (contentScrollRef.current) {
          contentScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 0);
    }
  };

  const handleImportComplete = (result: { imported: number; failed: number; errors: string[]; warnings?: string[]; importSessionId?: string; totalRows?: number; skippedDuplicates?: number; skippedZeroAmounts?: number }) => {
    setImportResult(result);
    setCurrentStep(4);
  };

  const handleStep4Close = () => {
    if (onImportComplete) {
      onImportComplete();
    }
    // Reset modal state
    resetModal();
    onClose();
  };

  const resetModal = () => {
    setMode('automatic');
    setCurrentStep(1);
    setSelectedFile(null);
    setCsvColumns([]);
    setCsvRows([]);
    setMappings([]);
    setImportResult(null);
    setSelectedAccountId(accountId || '');
  };

  const handleManualTransactionComplete = () => {
    if (onImportComplete) {
      onImportComplete();
    }
    resetModal();
    onClose();
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-modal-title"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-[70]" 
        onClick={handleClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-start justify-center pt-14 pb-4 px-4 relative z-[71]">
        <div
          className="relative w-full max-w-5xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-800 flex flex-col max-h-[calc(100vh-5rem)]"
          onClick={(e) => e.stopPropagation()}
        >
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Left Sidebar - Vertical Stepper (only for automatic mode) */}
          {mode === 'automatic' && (
            <div className="w-48 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex-shrink-0">
              <div className="p-6">
                <div className="flex flex-col space-y-6">
                  {[
                    { num: 1, label: 'Basic Settings' },
                    { num: 2, label: 'Mapping' },
                    { num: 3, label: 'Importing' },
                    { num: 4, label: 'Summary' }
                  ].map((step, index) => (
                    <React.Fragment key={step.num}>
                      <div className="flex items-start space-x-3">
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                            currentStep >= step.num
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}>
                            {step.num}
                          </div>
                          {index < 3 && (
                            <div className={`w-0.5 flex-1 mt-2 transition-colors ${
                              currentStep > step.num ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
                            }`} style={{ minHeight: '2rem' }} />
                          )}
                        </div>
                        <div className="flex-1 pt-2">
                          <span className={`text-sm font-medium block ${
                            currentStep >= step.num
                              ? 'text-primary-600 dark:text-primary-400'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {step.label}
                          </span>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 id="import-modal-title" className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    Add Transactions
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Import from CSV file or manually enter a transaction
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  aria-label="Close modal"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Mode Toggle - Only show on step 1 */}
              {currentStep === 1 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Import Method
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setMode('automatic')}
                    disabled={false}
                    className={`
                      relative p-4 border-2 rounded-lg text-left transition-all duration-200
                      ${mode === 'automatic'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                      }
                      cursor-pointer hover:shadow-sm
                      focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                    `}
                    aria-pressed={mode === 'automatic'}
                    aria-describedby="csv-import-description"
                  >
                    <input
                      type="radio"
                      name="mode"
                      value="automatic"
                      checked={mode === 'automatic'}
                      onChange={() => setMode('automatic')}
                      className="sr-only"
                    />
                    {mode === 'automatic' && (
                      <div className="absolute top-2 right-2 w-5 h-5 bg-primary-600 rounded-full flex items-center justify-center">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                    <div className="flex items-start space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        mode === 'automatic'
                          ? 'bg-primary-100 dark:bg-primary-900/30'
                          : 'bg-gray-100 dark:bg-gray-700'
                      }`}>
                        <Upload className={`h-5 w-5 ${
                          mode === 'automatic'
                            ? 'text-primary-600 dark:text-primary-400'
                            : 'text-gray-600 dark:text-gray-400'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">CSV Import</div>
                        <p id="csv-import-description" className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Upload and import multiple transactions from a CSV file
                        </p>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('manual')}
                    disabled={false}
                    className={`
                      relative p-4 border-2 rounded-lg text-left transition-all duration-200
                      ${mode === 'manual'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 hover:border-gray-300 dark:border-gray-600 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                      }
                      cursor-pointer hover:shadow-sm
                      focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                    `}
                    aria-pressed={mode === 'manual'}
                    aria-describedby="manual-entry-description"
                  >
                    <input
                      type="radio"
                      name="mode"
                      value="manual"
                      checked={mode === 'manual'}
                      onChange={() => setMode('manual')}
                      className="sr-only"
                    />
                    {mode === 'manual' && (
                      <div className="absolute top-2 right-2 w-5 h-5 bg-primary-600 rounded-full flex items-center justify-center">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                    <div className="flex items-start space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        mode === 'manual'
                          ? 'bg-primary-100 dark:bg-primary-900/30'
                          : 'bg-gray-100 dark:bg-gray-700'
                      }`}>
                        <Pencil className={`h-5 w-5 ${
                          mode === 'manual'
                            ? 'text-primary-600 dark:text-primary-400'
                            : 'text-gray-600 dark:text-gray-400'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Manual Entry</div>
                        <p id="manual-entry-description" className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Create a single transaction by entering details manually
                        </p>
                      </div>
                    </div>
                  </button>
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div
              ref={contentScrollRef}
              className={
                mode === 'manual'
                  ? 'flex-1 flex flex-col min-h-0 overflow-hidden'
                  : 'flex-1 min-h-0 overflow-y-auto px-6 py-4'
              }
            >
              {mode === 'manual' ? (
                <ManualTransactionForm
                  accountId={accountId}
                  accounts={accounts}
                  onComplete={handleManualTransactionComplete}
                  onCancel={handleClose}
                />
              ) : (
                <>
                  {currentStep === 1 && (
                    <ImportStep1FileUpload
                      onFileSelected={handleFileSelected}
                      selectedFile={selectedFile}
                      accounts={accounts}
                      selectedAccountId={selectedAccountId}
                      onAccountChange={setSelectedAccountId}
                      accountDisabled={accountDisabled} // Use explicit prop to control account selection
                      loadingAccounts={loadingAccounts}
                      onNext={handleStep1Next}
                      onCancel={handleClose}
                    />
                  )}

                  {currentStep === 2 && (
                    <ImportStep2ColumnMapping
                      csvColumns={csvColumns}
                      csvRows={csvRows}
                      mappings={mappings}
                      onMappingsChange={setMappings}
                      onNext={handleStep2Next}
                      onBack={() => setCurrentStep(1)}
                      onCancel={handleClose}
                    />
                  )}

                  {currentStep === 3 && (
                    <ImportStep3ReviewImport
                      csvRows={csvRows}
                      csvColumns={csvColumns}
                      mappings={mappings}
                      accountId={selectedAccountId}
                      fileName={selectedFile?.name || ''}
                      fileSize={selectedFile?.size}
                      onImportComplete={handleImportComplete}
                      onBack={() => setCurrentStep(2)}
                      onCancel={handleClose}
                    />
                  )}

                  {currentStep === 4 && importResult && (
                    <ImportStep4Complete
                      fileName={selectedFile?.name || ''}
                      accountId={selectedAccountId}
                      imported={importResult.imported}
                      failed={importResult.failed}
                      errors={importResult.errors}
                      warnings={importResult.warnings || []}
                      importSessionId={importResult.importSessionId}
                      totalRows={importResult.totalRows}
                      skippedDuplicates={importResult.skippedDuplicates}
                      skippedZeroAmounts={importResult.skippedZeroAmounts}
                      onClose={handleStep4Close}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

